import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import type { Location, LocationsPayload, Product, ProductsPayload, WarehouseAddResult, WarehouseUploadResult } from "../../types";
import { formatNumber, locationLabel, productLabel } from "../../display";

export function AddInventoryWorkspace({ csrfToken }: { csrfToken: string }) {
  const [warehouses, setWarehouses] = useState<Location[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("0");
  const [expiry, setExpiry] = useState("");
  const [lotCode, setLotCode] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadWarehouses = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<LocationsPayload>("/api/locations");
      const warehouseRows = (payload.locations || []).filter(location => ["COMD", "COMW"].includes(String(location.code || "").toUpperCase()));
      setWarehouses(warehouseRows);
      setSelectedWarehouse(current => current || String(warehouseRows[0]?.id || ""));
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Warehouse locations could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProducts = useCallback(async (warehouseId: string) => {
    if (!warehouseId) {
      setProducts([]);
      setSelectedProduct("");
      return;
    }
    setLoading(true);
    try {
      const payload = await apiGet<ProductsPayload>(`/api/products?location_id=${encodeURIComponent(warehouseId)}`);
      const activeProducts = (payload.products || []).filter(product => Number((product as { active?: number }).active ?? 1) !== 0);
      setProducts(activeProducts);
      setSelectedProduct(current => activeProducts.some(product => String(product.id) === current) ? current : String(activeProducts[0]?.id || ""));
    } catch (err) {
      setProducts([]);
      setSelectedProduct("");
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory items could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    void loadProducts(selectedWarehouse);
  }, [loadProducts, selectedWarehouse]);

  const submitAddStock = useCallback(async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setMessage(null);
    if (!selectedWarehouse) {
      setMessage({ variant: "danger", title: "Choose a warehouse before adding stock." });
      return;
    }
    if (!selectedProduct) {
      setMessage({ variant: "danger", title: "Choose an inventory item before adding stock." });
      return;
    }
    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setMessage({ variant: "danger", title: "Quantity must be greater than zero." });
      return;
    }
    const parsedUnitCost = Number(unitCost || 0);
    if (!Number.isFinite(parsedUnitCost) || parsedUnitCost < 0) {
      setMessage({ variant: "danger", title: "Unit cost cannot be negative." });
      return;
    }
    setSaving(true);
    try {
      const result = await apiPost<WarehouseAddResult>("/api/warehouse-inventory/add", {
        location_id: selectedWarehouse,
        product_id: selectedProduct,
        quantity: parsedQuantity,
        unit_cost: parsedUnitCost,
        expires_at: expiry,
        lot_code: lotCode
      }, csrfToken);
      setMessage({ variant: "success", title: `Added ${formatNumber(result.quantity)} to ${result.location_code} inventory.` });
      setQuantity("");
      setUnitCost("0");
      setLotCode("");
      await loadProducts(selectedWarehouse);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Stock could not be added." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, expiry, loadProducts, lotCode, quantity, selectedProduct, selectedWarehouse, unitCost]);

  const submitUpload = useCallback(async (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setMessage(null);
    if (!csvFile) {
      setMessage({ variant: "danger", title: "Choose an inventory CSV file first." });
      return;
    }
    setSaving(true);
    try {
      const csvText = await csvFile.text();
      const result = await apiPost<WarehouseUploadResult>("/api/warehouse-inventory/upload", {
        filename: csvFile.name,
        csv_text: csvText
      }, csrfToken);
      setMessage({ variant: "success", title: `Uploaded ${formatNumber(result.imported_rows)} inventory row(s) from ${result.filename}.` });
      setCsvFile(null);
      await loadProducts(selectedWarehouse);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Stock file could not be imported." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, csvFile, loadProducts, selectedWarehouse]);

  const selectedWarehouseCode = warehouses.find(location => String(location.id) === selectedWarehouse)?.code || "";

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-add-inventory-view" className="pf-toj-add-inventory">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
          <h1>Add to Inventory</h1>
            <p>Add received stock to COMD or COMW first. Create or import item records only when the item is new.</p>
          </FlexItem>
          {loading && (
            <FlexItem>
              <Spinner size="md" aria-label="Loading Add to Inventory" />
            </FlexItem>
          )}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <div className="pf-toj-work-grid">
          <Card className="pf-toj-work-card pf-toj-primary-work-card">
            <CardBody>
              <h2>Add stock quantity</h2>
              <p className="pf-toj-muted">Use this when stock has arrived and the item already exists in the inventory list.</p>
              <Form onSubmit={event => void submitAddStock(event)} className="pf-toj-form">
                <FormGroup label="Warehouse" isRequired fieldId="pf-add-inventory-warehouse">
                  <FormSelect
                    id="pf-add-inventory-warehouse"
                    value={selectedWarehouse}
                    onChange={(_event, value) => setSelectedWarehouse(String(value))}
                    aria-label="Warehouse"
                  >
                    {warehouses.map(location => (
                      <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />
                    ))}
                  </FormSelect>
                  <FormHelperText>{selectedWarehouseCode ? `Items shown are valid for ${selectedWarehouseCode}.` : "Only COMD and COMW can receive warehouse stock here."}</FormHelperText>
                </FormGroup>
                <FormGroup label="Existing item" isRequired fieldId="pf-add-inventory-product">
                  <FormSelect
                    id="pf-add-inventory-product"
                    value={selectedProduct}
                    onChange={(_event, value) => setSelectedProduct(String(value))}
                    aria-label="Existing item"
                  >
                    {products.map(product => (
                      <FormSelectOption key={product.id} value={String(product.id)} label={productLabel(product)} />
                    ))}
                  </FormSelect>
                  <FormHelperText>{products.length ? `${formatNumber(products.length)} item(s) available for this warehouse.` : "No items are available for this warehouse."}</FormHelperText>
                </FormGroup>
                <div className="pf-toj-field-row">
                  <FormGroup label="Quantity" isRequired fieldId="pf-add-inventory-quantity">
                    <TextInput
                      id="pf-add-inventory-quantity"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={quantity}
                      onChange={(_event, value) => setQuantity(String(value))}
                    />
                  </FormGroup>
                  <FormGroup label="Unit cost" fieldId="pf-add-inventory-unit-cost">
                    <TextInput
                      id="pf-add-inventory-unit-cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitCost}
                      onChange={(_event, value) => setUnitCost(String(value))}
                    />
                  </FormGroup>
                  <FormGroup label="Expiry" fieldId="pf-add-inventory-expiry">
                    <TextInput
                      id="pf-add-inventory-expiry"
                      type="date"
                      value={expiry}
                      onChange={(_event, value) => setExpiry(String(value))}
                    />
                  </FormGroup>
                </div>
                <FormGroup label="Lot code" fieldId="pf-add-inventory-lot-code">
                  <TextInput
                    id="pf-add-inventory-lot-code"
                    value={lotCode}
                    onChange={(_event, value) => setLotCode(String(value))}
                    placeholder="Optional"
                  />
                </FormGroup>
                <Button type="submit" variant="primary" isLoading={saving} isDisabled={saving || loading || !products.length} onClick={event => void submitAddStock(event)}>
                  Add stock
                </Button>
              </Form>
            </CardBody>
          </Card>
          <Card className="pf-toj-work-card">
            <CardBody>
              <h2>Import stock quantity file</h2>
              <p className="pf-toj-muted">Upload many received stock quantities at once.</p>
              <Form onSubmit={event => void submitUpload(event)} className="pf-toj-form">
                <FormGroup label="Inventory CSV" isRequired fieldId="pf-add-inventory-csv">
                  <input
                    id="pf-add-inventory-csv"
                    className="pf-toj-file-input"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={event => setCsvFile(event.currentTarget.files?.[0] || null)}
                  />
                  <FormHelperText>Required columns: location_code, sku, quantity. Optional: unit_cost, lot_code, expires_at.</FormHelperText>
                </FormGroup>
                <Button type="submit" variant="primary" isLoading={saving} isDisabled={saving || !csvFile} onClick={event => void submitUpload(event)}>
                  Import stock file
                </Button>
              </Form>
              <Divider className="pf-toj-soft-divider" />
              <div className="pf-toj-secondary-actions">
                <Button variant="secondary" onClick={() => document.querySelector<HTMLButtonElement>("#addInventoryItemAction")?.click()}>
                  Create item
                </Button>
                <Button variant="secondary" onClick={() => document.querySelector<HTMLElement>("[data-add-inventory-target='inventoryItemImportReviewPanel']")?.click()}>
                  Review imported items
                </Button>
                <Button variant="secondary" onClick={() => document.querySelector<HTMLElement>("[data-add-inventory-target='itemVisibilityGroupPanel']")?.click()}>
                  Item visibility
                </Button>
                <Button variant="secondary" onClick={() => document.querySelector<HTMLElement>("[data-add-inventory-target='itemWarehouseMappingPanel']")?.click()}>
                  Warehouse source
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageSection>
  );
}
